import {useCompanyTariffGuard} from '../../../../modules/company-tariff';
import {FC, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useNavigate} from 'react-router-dom';
import {useRecoilStateLoadable, useRecoilValue, useSetRecoilState} from 'recoil';

import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SearchIcon from '@mui/icons-material/Search';
import LoadingButton from '@mui/lab/LoadingButton';
import {FormControl, InputLabel, MenuItem, Select, TextField} from '@mui/material';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import {green} from '@mui/material/colors';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import {alpha} from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {companiesFilterQueryTrigger, companyPageable} from '../../../../store/company/atoms';
import {companiesQuery, companySelectedIdsSelector} from '../../../../store/company/selectors';
import DeleteCompanies from './DeleteCompanies';
import {LoadableHelpers} from '../../../../shared/helpers/loadableHelpers';

interface ITableToolbar {
  isLoading: boolean;
}

const TableToolbar: FC<ITableToolbar> = ({isLoading}) => {
  const {checkTariff, tariffLoading} = useCompanyTariffGuard();
  const [openDelete, setOpenDelete] = useState(false);
  const [filterMode, setFilterMode] = useState(false);
  const {
    getValues,
    formState: {errors},
    handleSubmit,
    register,
    reset,
  } = useForm();
  const [loadable] = useRecoilStateLoadable(companiesQuery);
  const setCompanyFilter = useSetRecoilState(companiesFilterQueryTrigger);
  const setCompanyPageable = useSetRecoilState(companyPageable);
  const selected = useRecoilValue(companySelectedIdsSelector);
  const navigate = useNavigate();

  const toAddCompanyPage = () => {
    if (!checkTariff()) return;
    navigate('/customers/addCompany');
  };

  const toggleOpenDelete = () => setOpenDelete(!openDelete);

  const selectedLength = selected.length;

  const resetSearchField = () => {
    reset({filter: '', filterBy: ''});
    setFilterMode(false);
    setCompanyFilter(null);
    setCompanyPageable((prev) => ({...prev, page: 0}));
  };

  const onSubmit = async () => {
    const {filter, filterBy} = getValues();
    setCompanyFilter({[filterBy]: filter});
    setCompanyPageable((prev) => ({...prev, page: 0}));
  };

  return (
    <>
      <Toolbar
        sx={{
          pl: {sm: 2},
          pr: {xs: 1, sm: 1},
          ...(selectedLength > 0 && {
            bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
          }),
        }}
      >
        {selectedLength > 0 ? (
          <Typography sx={{flex: '1 1 100%'}} color="inherit" variant="subtitle1" component="div">
            {selectedLength} выбрано
          </Typography>
        ) : filterMode ? (
          <Typography sx={{flex: '1 1 30%'}} variant="h6" component="div">
            Фильтр компаний
          </Typography>
        ) : (
          <Stack direction="row" alignItems="center" spacing={2} sx={{flex: '1 1 100%'}}>
            <Typography variant="h6" id="tableTitle" component="div">
              Компании
            </Typography>
            {isLoading && <CircularProgress size="24px" />}
          </Stack>
        )}
        {selectedLength > 0 ? (
          <Tooltip title="Удалить выбранные">
            <IconButton onClick={toggleOpenDelete}>
              <DeleteForeverIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Stack spacing={2} direction="row">
            {filterMode ? (
              <form onSubmit={handleSubmit(onSubmit)}>
                <Stack direction="row" gap={2}>
                  <FormControl fullWidth size="small" sx={{width: '360px'}} error={!!errors.filterBy}>
                    <InputLabel id="select-label">Критерий поиска</InputLabel>
                    <Select
                      labelId="select-label"
                      id="demo-simple-select"
                      label="Критерий поиска"
                      size="small"
                      {...register('filterBy', {required: true})}
                    >
                      <MenuItem value="name">Название компании</MenuItem>
                      <MenuItem value="inn">ИНН</MenuItem>
                      <MenuItem value="ogrn">ОГРН</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    label="Значение поиска"
                    placeholder="Введите значение"
                    size="small"
                    fullWidth
                    {...register('filter', {required: true})}
                    error={!!errors.filter}
                  />

                  <LoadingButton
                    sx={{padding: '6px 22px'}}
                    loading={LoadableHelpers.isLoading(loadable)}
                    variant="contained"
                    type="submit"
                  >
                    Искать
                  </LoadingButton>
                  <LoadingButton
                    sx={{padding: '6px 22px'}}
                    loading={LoadableHelpers.isLoading(loadable)}
                    onClick={resetSearchField}
                    variant="outlined"
                    color="error"
                  >
                    Отмена
                  </LoadingButton>
                </Stack>
              </form>
            ) : (
              <>
                <Tooltip title="Фильтр">
                  <IconButton onClick={() => setFilterMode(true)}>
                    <SearchIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Добавить компанию в систему">
                  <Button
                    onClick={toAddCompanyPage}
                    disabled={tariffLoading}
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    sx={{
                      height: '40px',
                      fontSize: '0.87rem',
                      whiteSpace: 'nowrap',
                      letterSpacing: '0',
                      backgroundColor: green[500],
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: green[600],
                      },
                    }}
                    data-testid="addClient"
                  >
                    Добавить компанию
                  </Button>
                </Tooltip>
              </>
            )}
          </Stack>
        )}
      </Toolbar>
      <DeleteCompanies open={openDelete} selectedIDs={selected} handleClose={toggleOpenDelete} />
    </>
  );
};

export default TableToolbar;
